// @flow
import React from 'react';
import {connect} from 'react-redux';
import gql from 'graphql-tag';
import {injectIntl, type IntlShape} from 'react-intl';
import classNames from 'classnames';
import {compose} from 'redux';
import HAPager from '@homeaway/react-pager';
import routerActions from '../../actions/router';
import {trigger as edapTrigger, GENERIC_EVENT} from '../../utils/edapWrapper';
import resultsCountText from '../../utils/resultsCountText';
import {makePageURL} from '../../utils/routerUtils';
import type {State as ReduxState} from '../../reducers/index';
import {VRBO} from '../../constants/sites';

import './Pager.less';

export const fragments = {
    hitCollection: gql`
        fragment Pager on Results {
            fromRecord
            toRecord
            pageSize
            pageCount
            page
            resultCount
        }
    `
};
export type Shape = {|
    fromRecord: number,
    toRecord: number,
    resultCount: number,
    page: number,
    pageCount: number
|};
type ConnectorProps = {
    nextPageUrl: string,
    prevPageUrl: string,
    brand: string
};
type OwnProps = {
    pager: Shape,
    intl: IntlShape,
    onPageChange: (number) => any
};
export type Props = OwnProps & ConnectorProps;

const defaultPager: Shape = {
    fromRecord: 0,
    toRecord: 0,
    resultCount: 0,
    page: 0,
    pageCount: 0
};

/**
 * Wraps the stock homeaway baseline pager with a search-friendly interface
 */
export const Pager = ({
    intl,
    // eslint-disable-next-line no-unused-vars
    onPageChange = (page: number) => {},
    pager: {fromRecord, toRecord, resultCount, page, pageCount} = defaultPager,
    nextPageUrl,
    prevPageUrl,
    brand
}: Props) => {
    const hasNext = page < pageCount;
    const hasPrev = page > 1;

    const pageEvent = () => {
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'pagination'
        });
    };
    /* We pass in the whole router query on the page change event
    because redux-little-router currently does not handle
    persistQuery properly */
    // https://github.com/FormidableLabs/redux-little-router/issues/224
    const onNextClick = () => {
        const newPage = page + 1;
        pageEvent();
        onPageChange(newPage);
    };

    const onPrevClick = () => {
        const newPage = page - 1;
        pageEvent();
        onPageChange(newPage);
    };

    if (!resultCount) {
        return null;
    }

    const classes = classNames('HAPager', {
        vrbo: brand === VRBO.toUpperCase()
    });

    const pager = (
        <HAPager
            alignCenter
            hasNext={hasNext}
            hasPrev={hasPrev}
            nextUrl={hasNext ? nextPageUrl : null}
            prevUrl={hasPrev ? prevPageUrl : null}
            pageCount={resultsCountText(
                fromRecord,
                toRecord,
                resultCount,
                intl
            )}
            onNextClick={onNextClick}
            onPrevClick={onPrevClick}
        />
    );

    return (
        <div className={classes} data-wdio="HAPager" role="region">
            {pager}
        </div>
    );
};

const mapStateToProps = (
    state: ReduxState,
    {pager: {page}}: OwnProps
): ConnectorProps => ({
    nextPageUrl: makePageURL(page + 1, state),
    prevPageUrl: makePageURL(page - 1, state),
    brand: state.system.brand
});

const mapDispatchToProps = {
    onPageChange: routerActions.page
};

export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(Pager);
